import {
  CREATE_RATING,
  DELETE_RATING,
  FETCH_PACKAGE_DEPLOYMENT_SUBSCRIPTIONS,
  FETCH_RATING_AVERAGE_BY_CATALOG,
  FETCH_RATING_BY_ID,
  FETCH_RATINGS,
  FETCH_RATINGS_BY_CATALOG,
  SEARCH_HUB_CATALOGS,
  SEARCH_RATINGS,
  UPDATE_RATING,
} from "@apiUtils";
import {
  mutationFetch,
  type SearchPayload,
  type UseSearchQueryOptions,
  useSearchQuery,
} from "@atlas/sdk";
import type { IHubRating, IHubRatingAverageByCatalogResponse } from "@models";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import {
  getHubCatalogByIdKey,
  HUB_CATALOG_BY_ID_AND_VERSION_KEY,
  HUB_CATALOG_BY_ID_KEY,
  HUB_CATALOGS_KEY,
} from "./HubCatalogs";

export const HUB_RATINGS_KEY = "hubRatingsKey";
export const HUB_RATING_BY_ID_KEY = "hubRatingByIdKey";
export const HUB_RATINGS_BY_CATALOG_KEY = "hubRatingsByCatalogKey";
export const HUB_RATING_AVERAGE_BY_CATALOG_KEY = "hubRatingAverageByCatalogKey";

export const getHubRatingsKey = () => [HUB_RATINGS_KEY] as const;

export const getHubRatingByIdKey = (ratingId: number) =>
  [HUB_RATING_BY_ID_KEY, ratingId] as const;

export const getHubRatingsByCatalogKey = (catalogId: number) =>
  [HUB_RATINGS_BY_CATALOG_KEY, catalogId] as const;

export const getHubRatingAverageByCatalogKey = (catalogId: number) =>
  [HUB_RATING_AVERAGE_BY_CATALOG_KEY, catalogId] as const;

export const useHubRatings = (
  options?: Partial<
    UseQueryOptions<
      IHubRating[],
      AxiosError,
      IHubRating[],
      ReturnType<typeof getHubRatingsKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubRatingsKey(),
    queryFn: async () => {
      return mutationFetch<IHubRating[]>({
        url: FETCH_RATINGS,
        method: "GET",
      });
    },
    ...options,
  });
};

export const useHubRating = (
  ratingId: number,
  options?: Partial<
    UseQueryOptions<
      IHubRating,
      AxiosError,
      IHubRating,
      ReturnType<typeof getHubRatingByIdKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubRatingByIdKey(ratingId),
    queryFn: async () => {
      return mutationFetch<IHubRating>({
        url: FETCH_RATING_BY_ID.replace(":ratingId", String(ratingId)),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && ratingId > 0,
  });
};

export const useHubRatingsByCatalog = (
  catalogId: number,
  options?: Partial<
    UseQueryOptions<
      IHubRating[],
      AxiosError,
      IHubRating[],
      ReturnType<typeof getHubRatingsByCatalogKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubRatingsByCatalogKey(catalogId),
    queryFn: async () => {
      return mutationFetch<IHubRating[]>({
        url: FETCH_RATINGS_BY_CATALOG.replace(":catalogId", String(catalogId)),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && catalogId > 0,
  });
};

export const useHubRatingAverageByCatalog = (
  catalogId: number,
  options?: Partial<
    UseQueryOptions<
      IHubRatingAverageByCatalogResponse,
      AxiosError,
      IHubRatingAverageByCatalogResponse,
      ReturnType<typeof getHubRatingAverageByCatalogKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubRatingAverageByCatalogKey(catalogId),
    queryFn: async () => {
      return mutationFetch<IHubRatingAverageByCatalogResponse>({
        url: FETCH_RATING_AVERAGE_BY_CATALOG.replace(
          ":catalogId",
          String(catalogId),
        ),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && catalogId > 0,
  });
};

const createHubRating = (body: IHubRating): Promise<IHubRating> => {
  return mutationFetch<IHubRating>({
    url: CREATE_RATING,
    method: "POST",
    body,
  });
};

const updateHubRating = (
  ratingId: number,
  body: IHubRating,
): Promise<IHubRating> => {
  return mutationFetch<IHubRating>({
    url: UPDATE_RATING.replace(":ratingId", String(ratingId)),
    method: "PUT",
    body,
  });
};

const deleteHubRating = (ratingId: number): Promise<void> => {
  return mutationFetch<void>({
    url: DELETE_RATING.replace(":ratingId", String(ratingId)),
    method: "DELETE",
  });
};

export const useCreateHubRating = (
  options?: UseMutationOptions<IHubRating, AxiosError, IHubRating>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createHubRating,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [HUB_RATINGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_RATINGS] });
      if (data.catalogId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubRatingsByCatalogKey(data.catalogId),
        });
        queryClient.invalidateQueries({
          queryKey: getHubRatingAverageByCatalogKey(data.catalogId),
        });
        queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
        queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CATALOGS] });
        queryClient.invalidateQueries({
          queryKey: [FETCH_PACKAGE_DEPLOYMENT_SUBSCRIPTIONS],
        });
        queryClient.invalidateQueries({
          queryKey: getHubCatalogByIdKey(data.catalogId),
        });
        queryClient.invalidateQueries({
          queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY, data.catalogId],
        });
      }
    },
    ...options,
  });
};

export const useUpdateHubRating = (
  options?: UseMutationOptions<IHubRating, AxiosError, IHubRating>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: IHubRating) => {
      const ratingId = body.ratingId;
      if (ratingId == null) {
        throw new Error("ratingId is required for update");
      }
      return updateHubRating(ratingId, body);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [HUB_RATINGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_RATINGS] });
      if (data.ratingId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubRatingByIdKey(data.ratingId),
        });
      }
      if (data.catalogId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubRatingsByCatalogKey(data.catalogId),
        });
        queryClient.invalidateQueries({
          queryKey: getHubRatingAverageByCatalogKey(data.catalogId),
        });
        queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
        queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CATALOGS] });
        queryClient.invalidateQueries({
          queryKey: [FETCH_PACKAGE_DEPLOYMENT_SUBSCRIPTIONS],
        });
        queryClient.invalidateQueries({
          queryKey: getHubCatalogByIdKey(data.catalogId),
        });
        queryClient.invalidateQueries({
          queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY, data.catalogId],
        });
      }
    },
    ...options,
  });
};

export const useDeleteHubRating = (
  options?: UseMutationOptions<void, AxiosError, number>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHubRating,
    onSuccess: (_, ratingId) => {
      queryClient.invalidateQueries({ queryKey: [HUB_RATINGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_RATINGS] });
      queryClient.invalidateQueries({
        queryKey: getHubRatingByIdKey(ratingId),
      });
      queryClient.invalidateQueries({
        queryKey: [HUB_RATINGS_BY_CATALOG_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [HUB_RATING_AVERAGE_BY_CATALOG_KEY],
      });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CATALOGS] });
      queryClient.invalidateQueries({
        queryKey: [FETCH_PACKAGE_DEPLOYMENT_SUBSCRIPTIONS],
      });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOG_BY_ID_KEY] });
      queryClient.invalidateQueries({
        queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY],
      });
    },
    ...options,
  });
};

export const useSearchHubRatings = (
  payload: SearchPayload,
  options?: Omit<UseSearchQueryOptions<IHubRating>, "url" | "payload">,
) => {
  return useSearchQuery<IHubRating>({
    url: SEARCH_RATINGS,
    payload,
    ...options,
  });
};
